import { useIsLaptopOrHigher } from "@/libs/utils";
import { VideoAsset } from "@/src/components/VideoAsset";
import { CfAssetContentType } from "@/src/types";
import ArtDirectedNextImage from "../ArtDirectedNextImage";
import Image, { StaticImageData } from "next/image";
import { isAssetSVG } from "../cards/components/ProductCard/components/ProductCardThumbnail/helpers";
import {
  ASSET_MEDIA_QUERY_BREAKPOINT,
  ASSET_DEFAULT_HEIGHT,
  ASSET_DEFAULT_WIDTH,
} from "./constants";
import { getBlurDataURL, isUnoptimizedImageEnv } from "./helpers";
import React from "react";
import {
  Asset as CfAsset,
  ContentTypeAsset,
} from "nvs-types/dist/types/generated/contentful";

export type VideoOptions = {
  autoPlay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  ref?: React.MutableRefObject<HTMLVideoElement | null>;
  onLoad?: () => void;
  onEnded?: () => void;
};

export type ImageOptions = {
  sizes?: string;

  onLoad?: () => void;
};

type AssetProps = {
  /**
   * Note ContentTypeAsset is the asset types we should be using from Contentful
   * but we also support the old CfAssetContentType type for backwards compatibility
   **/
  asset: CfAssetContentType | ContentTypeAsset;
  className?: string;
  options?: VideoOptions & ImageOptions;
};

const ASSET_DEFAULT_DESCRIPTION = "NVS Image";

const Asset: React.FC<AssetProps> = ({
  asset: cfAsset,
  className,
  options = {} as VideoOptions & ImageOptions,
}) => {
  const isVideo = cfAsset?.asset?.contentType?.includes("video");
  const isLaptopOrHigher = useIsLaptopOrHigher();
  const blurDataURL = getBlurDataURL(cfAsset?.asset as CfAsset);

  if (isVideo) {
    const { videoFallbackImage, videoFallbackImageMobile, loopVideo } = cfAsset;
    const poster =
      (isLaptopOrHigher
        ? videoFallbackImage?.url
        : videoFallbackImageMobile?.url ?? videoFallbackImage?.url) ?? "";
    return (
      <VideoAsset
        ref={options?.ref}
        onLoadedData={options?.onLoad}
        onEnded={options?.onEnded}
        loop={options?.loop ?? !!loopVideo}
        {...options}
        src={
          (!isLaptopOrHigher && cfAsset.mobileAsset
            ? cfAsset.mobileAsset.url
            : cfAsset?.asset?.url) ?? ""
        }
        poster={poster}
        className={className}
        aria-label={cfAsset.description ?? ""}
      />
    );
  }

  const assetImageProps: StaticImageData = {
    src: cfAsset?.asset?.url || "",
    width: cfAsset?.asset?.width || ASSET_DEFAULT_WIDTH,
    height: cfAsset?.asset?.height || ASSET_DEFAULT_HEIGHT,
  };

  // Single source image
  if (cfAsset.asset && !cfAsset.mobileAsset) {
    return (
      <Image
        sizes={options?.sizes}
        {...assetImageProps}
        alt={cfAsset.description ?? ASSET_DEFAULT_DESCRIPTION}
        className={className}
        unoptimized={
          isAssetSVG(cfAsset?.asset?.url ?? "") || isUnoptimizedImageEnv()
        }
        placeholder="blur"
        blurDataURL={blurDataURL}
      />
    );
  }

  // Art directed image with mobile and desktop sources (i.e different aspect ratio source)
  return (
    <ArtDirectedNextImage
      mobileImage={{
        src: cfAsset.mobileAsset!.url ?? "",
        width: cfAsset.mobileAsset!.width || ASSET_DEFAULT_WIDTH,
        height: cfAsset.mobileAsset!.height || ASSET_DEFAULT_HEIGHT,
        blurDataURL,
      }}
      sizes={options?.sizes}
      desktopImage={{ ...assetImageProps, blurDataURL }}
      breakpoint={ASSET_MEDIA_QUERY_BREAKPOINT}
      description={cfAsset.description ?? ASSET_DEFAULT_DESCRIPTION}
      className={className}
      onLoad={options?.onLoad}
    />
  );
};

export default Asset;
